import { useEffect, useRef, useState } from 'react'

// Shared docking logic for elements that stick beneath the secondary nav.
// Measures the bottom of the pinned nav (or its tab bar on scroll-away-header
// pages, whose offset stays stable while the header scrolls away) so a sticky
// element can sit right under it, and reports whether the tracked element has
// reached that dock (isStuck) — for toggling a shadow/border once pinned.
export const useStickyNavOffset = () => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [navOffset, setNavOffset] = useState<number>(216)
  const [isStuck, setIsStuck] = useState<boolean>(false)

  useEffect(() => {
    const tabs = document.getElementById('secondaryNavTabs')
    const nav = tabs ?? document.getElementById('secondaryNav')
    // No nav to dock under (the public careers pages): stick to the viewport top.
    if (!nav) {
      setNavOffset(0)
      return
    }
    const update = () => {
      if (tabs) {
        setNavOffset(56 + tabs.offsetHeight)
        return
      }
      const rect = nav.getBoundingClientRect()
      if (rect.bottom > 0) setNavOffset(rect.bottom)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(nav)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const update = () => {
      if (!sentinelRef.current) return
      setIsStuck(
        sentinelRef.current.getBoundingClientRect().top < navOffset - 8
      )
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [navOffset])

  return { sentinelRef, navOffset, isStuck }
}
